import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from '../../../constants/apiConfig';
import {
  selectGroupGuid,
  selectUserGuid,
} from '../../../slices/commonSelectors';
import { useActualIncidentId } from './commonHooks';
import { keys as incidentActionKeys } from './incidentActionHooks';
import { keys as orgActionKeys } from './orgActionHooks';

export type ActionStatusCounts = Record<string, number>;

export type ActionRollupCounts = {
  total: number;
  by_status: ActionStatusCounts;
  by_priority: ActionStatusCounts;
  overdue: number;
  unassigned: number;
  blocked: number;
  due_this_shift: number;
};

export type ActionRollupGroup = ActionRollupCounts & {
  key: string;
  label: string;
};

export type ActionRollupGrouping = {
  key: string;
  label: string;
  groups: ActionRollupGroup[];
};

export type ActionRollup = ActionRollupCounts & {
  generated_at: string;
  /** End of the window `due_this_shift` counts against. */
  shift_ends_at: string;
  groupings: ActionRollupGrouping[];
};

/**
 * `null`/empty means every AOR; otherwise the rollup only counts these AORs'
 * actions.
 */
export type ActionRollupAorScope = readonly string[] | null | undefined;

function normalizeAorScope(aorIds: ActionRollupAorScope): string[] {
  return aorIds?.length ? [...aorIds].sort() : [];
}

/**
 * Extends the action list keys rather than sitting beside them, so that the
 * invalidation every action mutation already does reaches the summary above the
 * table by prefix. A roll-up that disagrees with the list underneath it is
 * worse than no roll-up.
 */
export const rollupKeys = {
  org: (groupGuid: string, userGuid: string, aorIds?: ActionRollupAorScope) =>
    [
      ...orgActionKeys.byGroupAndUser(groupGuid, userGuid),
      'rollup',
      ...(aorIds?.length ? [{ aorIds: normalizeAorScope(aorIds) }] : []),
    ] as const,
  incident: (
    incidentId: string,
    userGuid: string,
    aorIds?: ActionRollupAorScope
  ) =>
    [
      ...incidentActionKeys.byIncidentAndUser(incidentId, userGuid),
      'rollup',
      ...(aorIds?.length ? [{ aorIds: normalizeAorScope(aorIds) }] : []),
    ] as const,
} as const;

function rollupParams(aorIds: ActionRollupAorScope) {
  const params = new URLSearchParams();
  normalizeAorScope(aorIds).forEach((id) => params.append('aor_ids', id));
  return params;
}

export const useOrgActionRollup = (
  enabled = true,
  aorIds?: ActionRollupAorScope
) => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: rollupKeys.org(group_guid, user_guid, aorIds),
    queryFn: async () => {
      const resp = await axios.get<ActionRollup>(
        `${config.apiGateway.ics_org_actions}/${group_guid}/rollup`,
        { params: rollupParams(aorIds) }
      );
      return resp.data;
    },
    enabled: enabled && !!group_guid,
  });
};

export const useIncidentActionRollup = (
  incidentIdOverride?: string | null,
  enabled = true,
  aorIds?: ActionRollupAorScope
) => {
  const routeIncidentId = useActualIncidentId();
  const incidentId = incidentIdOverride || routeIncidentId;
  const user_guid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: rollupKeys.incident(incidentId, user_guid, aorIds),
    queryFn: async () => {
      const resp = await axios.get<ActionRollup>(
        `${config.apiGateway.ics_incident_actions}/${incidentId}/rollup`,
        { params: rollupParams(aorIds) }
      );
      return resp.data;
    },
    enabled: enabled && !!incidentId,
  });
};
